import type {
  ApplyBatchRequest, ApplyBatchResponse, ApplyRequest, AssistantStatus, AuthHeadersFn,
  ChatResponse, MapContextSnapshot, Scope,
} from './assistantTypes';
import { API_URL } from '../../config';

const BASE = `${API_URL}/api/assistant`;

async function fetchJson<T>(url: string, headers: HeadersInit): Promise<T> {
  const response = await fetch(url, { headers });
  if (!response.ok) {
    const err = await response.json().catch(() => ({ detail: `HTTP ${response.status}` }));
    throw new Error(err.detail || `Request failed: ${response.status}`);
  }
  return response.json();
}

async function mutate<T>(url: string, method: string, headers: HeadersInit, body?: unknown): Promise<T> {
  const response = await fetch(url, {
    method,
    headers,
    body: body !== undefined ? JSON.stringify(body) : undefined,
  });
  if (!response.ok) {
    const err = await response.json().catch(() => ({ detail: `HTTP ${response.status}` }));
    throw new Error(err.detail || `Request failed: ${response.status}`);
  }
  return response.json();
}

export async function fetchStatus(h: AuthHeadersFn): Promise<AssistantStatus> {
  return fetchJson<AssistantStatus>(`${BASE}/status`, h());
}

export interface ChatBody {
  message: string;
  conversation_id?: string | null;
  history: { role: 'user' | 'assistant'; content: string }[];
  context: MapContextSnapshot;
}

export async function sendChat(h: AuthHeadersFn, scope: Scope, body: ChatBody): Promise<ChatResponse> {
  return mutate<ChatResponse>(`${BASE}/chat?scope=${scope}`, 'POST', h(true), body);
}

export async function applyProposal(
  h: AuthHeadersFn, scope: Scope, body: ApplyRequest,
): Promise<{ kind: string; item: unknown }> {
  return mutate(`${BASE}/apply?scope=${scope}`, 'POST', h(true), body);
}

export async function applyBatch(
  h: AuthHeadersFn, scope: Scope, body: ApplyBatchRequest,
): Promise<ApplyBatchResponse> {
  return mutate<ApplyBatchResponse>(`${BASE}/apply-batch?scope=${scope}`, 'POST', h(true), body);
}

/** Switch the live feed to a marketplace model. Reuses the marketplace endpoint deliberately —
 *  that is where the licence gate lives, and a second activation path would be a second place for
 *  it to be forgotten. A 403 here is the unlicensed case and is expected, not exceptional. */
export async function activateModel(
  h: AuthHeadersFn, slug: string,
): Promise<{ status: string; slug: string; name: string }> {
  const response = await fetch(`${API_URL}/api/marketplace/models/${encodeURIComponent(slug)}/activate`, {
    method: 'POST', headers: h(true),
  });
  if (!response.ok) {
    const err = await response.json().catch(() => ({ detail: `HTTP ${response.status}` }));
    const error = new Error(err.detail || `Request failed: ${response.status}`);
    (error as Error & { status?: number }).status = response.status;
    throw error;
  }
  return response.json();
}

export type StreamEvent =
  | { type: 'tool'; phase: 'start' | 'end'; name: string; ok?: boolean }
  | { type: 'done'; payload: ChatResponse }
  | { type: 'error'; detail: string };

/**
 * One chat turn over SSE, reporting tool progress as it happens.
 *
 * Uses fetch + a ReadableStream rather than EventSource, which cannot POST and cannot set an
 * Authorization header — both non-negotiable here. The caller gets events through `onEvent` and the
 * final ChatResponse as the resolved value, so a caller that only wants the answer can ignore the
 * progress entirely.
 *
 * Throws on transport failure, which is what makes the non-streaming `sendChat` a usable fallback:
 * a proxy that buffers or strips text/event-stream fails here rather than hanging forever.
 */
export async function streamChat(
  h: AuthHeadersFn, scope: Scope, body: ChatBody,
  onEvent: (event: StreamEvent) => void,
  signal?: AbortSignal,
): Promise<ChatResponse> {
  const response = await fetch(`${BASE}/chat/stream?scope=${scope}`, {
    method: 'POST',
    headers: { ...(h(true) as Record<string, string>), Accept: 'text/event-stream' },
    body: JSON.stringify(body),
    signal,
  });

  if (!response.ok) {
    const err = await response.json().catch(() => ({ detail: `HTTP ${response.status}` }));
    throw new Error(err.detail || `Request failed: ${response.status}`);
  }
  if (!response.body) throw new Error('Streaming is not supported by this browser');

  const reader = response.body.getReader();
  const decoder = new TextDecoder();
  let buffer = '';
  let result: ChatResponse | null = null;
  let failure: string | null = null;

  const handleFrame = (frame: string) => {
    let event = 'message';
    const data: string[] = [];
    frame.split('\n').forEach((line) => {
      if (line.startsWith(':')) return;                       // heartbeat comment
      if (line.startsWith('event:')) event = line.slice(6).trim();
      else if (line.startsWith('data:')) data.push(line.slice(5).trim());
    });
    if (!data.length) return;

    let parsed: Record<string, unknown>;
    try {
      parsed = JSON.parse(data.join('\n'));
    } catch {
      return;                                                 // a torn frame is not fatal
    }

    if (event === 'tool') {
      onEvent({
        type: 'tool',
        phase: parsed.phase === 'end' ? 'end' : 'start',
        name: String(parsed.name || ''),
        ok: parsed.ok as boolean | undefined,
      });
    } else if (event === 'done') {
      result = parsed as unknown as ChatResponse;
      onEvent({ type: 'done', payload: result });
    } else if (event === 'error') {
      failure = String(parsed.detail || 'Assistant failed');
      onEvent({ type: 'error', detail: failure });
    }
  };

  // eslint-disable-next-line no-constant-condition
  while (true) {
    // eslint-disable-next-line no-await-in-loop
    const { done, value } = await reader.read();
    if (done) break;
    buffer += decoder.decode(value, { stream: true });
    let split = buffer.indexOf('\n\n');
    while (split !== -1) {
      handleFrame(buffer.slice(0, split));
      buffer = buffer.slice(split + 2);
      split = buffer.indexOf('\n\n');
    }
  }
  if (buffer.trim()) handleFrame(buffer);

  if (failure) throw new Error(failure);
  if (!result) throw new Error('The assistant closed the connection without replying');
  return result;
}
